/**
 * TOSS Web Application - Messenger Cache Helpers
 *
 * Plain (non-hook) helpers extracted from hooks/api/useMessenger.ts:
 * - chat-list preview text builders (mention tags, file/image labels)
 * - cache mutators used by the SignalR real-time layer
 *
 * The cache mutators are re-exported from '@/hooks/api/useMessenger';
 * consumers keep importing them from there.
 */

import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/app/queryClient';
import { useMessengerStore } from '@/store/messengerStore';
import { photoKey } from '@/features/applications/utils/inspectionPhotos';
import {
  MessageType,
  type ChatFull,
  type ChatMessage,
  type ChatListResponse,
  type MessageListResponse,
} from '@/types/messenger.types';
import type { QueryClient } from '@tanstack/react-query';

const MENTION_TAG_REGEX = /<@([a-f0-9-]+)>/g;

/** Check if a message is a file attachment whose content type starts with `prefix` */
function isFileOfType(
  msg: { messageType?: MessageType | string; fileContentType?: string },
  prefix: 'image/' | 'video/',
): boolean {
  return (msg.messageType === MessageType.File || msg.messageType === 'File')
    && !!msg.fileContentType?.startsWith(prefix);
}

/**
 * Generate last-message preview text for the chat list. «Фото» / «Видео» are the server's words
 * too (`GenerateMessagePreview`), so the optimistic preview does not change when the list refetches.
 */
export function messagePreviewText(msg: { content?: string; fileName?: string; messageType?: MessageType | string; fileContentType?: string }): string {
  if (msg.content) return msg.content;
  if (isFileOfType(msg, 'image/')) return 'Фото';
  if (isFileOfType(msg, 'video/')) return 'Видео';
  return msg.fileName || 'Файл';
}

/** Replace <@userId> tags with @FullName using cached chat member data */
export function stripMentionTagsForPreview(text: string, chatId: string, queryClient: QueryClient): string {
  const chatData = queryClient.getQueryData<ChatFull>(queryKeys.messenger.chat(chatId));
  const members = chatData?.members;
  if (!members || members.length === 0) {
    return text.replace(MENTION_TAG_REGEX, '@пользователь');
  }
  return text.replace(MENTION_TAG_REGEX, (_match, userId: string) => {
    const member = members.find((m) => m.userId === userId);
    return member ? `@${member.fullName}` : '@пользователь';
  });
}

// ============================================================================
// Cache Update Helpers (for SignalR real-time updates)
// ============================================================================

export function addMessageToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  message: ChatMessage,
  currentUserId?: string
): void {
  queryClient.setQueryData<{ pages: MessageListResponse[]; pageParams: unknown[] }>(
    queryKeys.messenger.messages(message.chatId),
    (oldData) => {
      if (!oldData) return oldData;
      const firstPage = oldData.pages[0];
      // Avoid duplicates
      const alreadyExists = oldData.pages.some((p) =>
        p.items?.some((m) => m.id === message.id)
      );
      if (alreadyExists) return oldData;
      return {
        ...oldData,
        pages: [
          { ...firstPage, items: [message, ...(firstPage?.items ?? [])] },
          ...oldData.pages.slice(1),
        ],
      };
    }
  );

  // Update chat list last message + unread count
  const activeChatId = useMessengerStore.getState().activeChatId;
  const isFromOtherUser = currentUserId && message.senderId !== currentUserId;
  const isActiveChat = message.chatId === activeChatId;

  queryClient.setQueriesData<ChatListResponse>(
    { queryKey: queryKeys.messenger.chats() },
    (oldData) => {
      if (!oldData?.items) return oldData;
      return {
        ...oldData,
        items: oldData.items.map((chat) =>
          chat.id === message.chatId
            ? {
                ...chat,
                lastMessage: messagePreviewText(message),
                lastMessageAt: message.createdAt,
                ...(isFromOtherUser && !isActiveChat
                  ? { unreadCount: chat.unreadCount + 1 }
                  : {}),
              }
            : chat
        ),
      };
    }
  );
}

export function updateMessageInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: string,
  messageId: string,
  updates: Partial<ChatMessage>
): void {
  queryClient.setQueryData<{ pages: MessageListResponse[]; pageParams: unknown[] }>(
    queryKeys.messenger.messages(chatId),
    (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          items: (page.items ?? []).map((msg) =>
            msg.id === messageId ? { ...msg, ...updates } : msg
          ),
        })),
      };
    }
  );
}

/**
 * `FileProcessed` (video release) carries `fileId`, not a `messageId` — a job can rewrite several
 * messages that share one blob (dedup twins) in one commit. Since 25.09.2026 the message names its
 * file itself (`ChatMessage.fileId`, design D13), and that is matched first; a message from a
 * server that predates the field is still matched by `photoKey(msg.fileUrl)`, the same id
 * extraction the batch meta lookup (`useFilesMeta`) uses. Both sides compare lower-cased: a GUID
 * is one id in either spelling.
 */
export function updateMessagesByFileIdInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: string,
  fileId: string,
  updates: Partial<ChatMessage>
): void {
  const wanted = fileId.toLowerCase();
  queryClient.setQueryData<{ pages: MessageListResponse[]; pageParams: unknown[] }>(
    queryKeys.messenger.messages(chatId),
    (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          items: (page.items ?? []).map((msg) =>
            namesFile(msg, wanted) ? { ...msg, ...updates } : msg
          ),
        })),
      };
    }
  );
}

/** Whether a cached message carries the file `wanted` (already lower-cased). */
function namesFile(msg: ChatMessage, wanted: string): boolean {
  if (msg.fileId && msg.fileId.toLowerCase() === wanted) return true;
  return !!msg.fileUrl && photoKey(msg.fileUrl) === wanted;
}

export function removeMessageFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: string,
  messageId: string,
): void {
  queryClient.setQueryData<{ pages: MessageListResponse[]; pageParams: unknown[] }>(
    queryKeys.messenger.messages(chatId),
    (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          items: page.items.filter((msg) => msg.id !== messageId),
        })),
      };
    }
  );
}

export function invalidateMessengerChats(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chats() });
}

/**
 * Whether `chatId` appears in any cached page of the chat list.
 *
 * A cache PATCH (`.map()` over the rows already there, as `addMessageToCache` and the
 * per-chat-unread handler both do) can only ever touch a row that is already in the cache — it
 * never adds one. A chat hidden by "delete for me", or one the caller has not fetched yet, is
 * exactly the case a patch cannot fix: the SignalR handler checks this after patching and falls
 * back to `invalidateMessengerChats` when the chat is missing, so the row reappears instead of
 * staying hidden until the next 30s refetch.
 */
export function isChatInCachedLists(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: string
): boolean {
  const lists = queryClient.getQueriesData<ChatListResponse>({ queryKey: queryKeys.messenger.chats() });
  return lists.some(([, data]) => data?.items?.some((chat) => chat.id === chatId));
}

export function updatePinnedMessageInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: string,
  pinnedMessage: ChatFull['pinnedMessage'],
): void {
  queryClient.setQueryData<ChatFull>(
    queryKeys.messenger.chat(chatId),
    (oldData) => {
      if (!oldData) return oldData;
      return { ...oldData, pinnedMessage };
    }
  );
}
